import { v4 } from 'uuid';
import { TopicSource } from 'app/copied/constants/common';

import makeApi, { makeRequestWithAbort, cancelRequest } from './api';

export enum ChatItemType {
  FORM = 'form',
  MESSAGE = 'message',
  DISCLAIMER_MESSAGE = 'disclaimer_message',
  TWILIO_CREDS = 'twilio_credentials',
  HIDE_PREV_MESSAGES = 'hide_previous_messages',
  TRANSFER_TO_LIVECHAT = 'transfer_to_livechat',
  TRANSFER_TO_VA = 'transfer_to_virtualassistant',
  DISABLE_TYPING_AREA = 'disable_typing_area',
  ENABLE_TYPING_AREA = 'enable_typing_area',
  REDIRECT_LINK = 'redirect_link',
  ANALYTICS_TAG = 'analytics_tag',
  CONTACT_FORM = 'contact_form',
  LOCATION_RESULTS = 'location_results',
  KB_ENTRY = 'kb_entry',
  STATIC_IMAGE = 'static_image',
  VIDEO = 'video',
  FEEDBACK = 'feedback_placeholder',
}

export enum ChatItemSourceType {
  SYSTEM = 'system',
  PATIENT = 'patient',
  PATIENT_CHOICE = 'patient_choice',
  NURSE = 'nurse',
  BOT = 'bot',
  SERVICE = 'service',
  ANALYTICS = 'analytics',
}

export enum ChatFormItemType {
  SINGE_CHOICE = 'single_choice',
  TEXT = 'text',
  INPUT = 'input',
}

export interface BaseChatItem {
  type: ChatItemType;
  source: ChatItemSourceType;
  delay_duration: number;
  visible: boolean;
  aiResponse: boolean;
  uuid: string;
  errorMessage?: boolean;
  topicSource?: TopicSource;
  queryTyped?: string;
  resultType?: string;
}

export enum ChatItemMessageType {
  MESSAGE = 'message',
  MESSAGE_WITH_AVATAR = 'message_with_avatar',
}

export interface ChatItemMessage extends BaseChatItem {
  type: ChatItemType.MESSAGE;
  message: string;
  message_type?: ChatItemMessageType;
}

export enum AnalyticsTagType {
  CHANGE = 'change',
  DISMISS = 'dismiss',
  ERROR = 'error',
  VIEW = 'view',
}

export enum AnalyticsTagOperator {
  ANY = 'any',
  EXACT = 'exact',
  IS_SET = 'is_set',
}

export interface AnalyticsTag {
  name: string;
  posthog_event_config: {
    type: AnalyticsTagType;
    properties: {
      operator: AnalyticsTagOperator;
      value: string;
    }[];
  };
  tag_pattern_id: string;
}

export interface ChatFormItem {
  item_type: ChatFormItemType;
  choices: string[];
  ref_id: string;
  placeholder: string;
  errors?: string[];
  value?: string;
  value_role?: string;
  analytics_tags?: AnalyticsTag[];
}

export enum ChatItemFormType {
  CONTACT_FORM = 'external_contact_forms',
}

export interface ChatItemForm extends BaseChatItem {
  type: ChatItemType.FORM;
  items: ChatFormItem[];
  ref_id: string;
  form_type?: ChatItemFormType;
  ref_slug: string;
  completed?: boolean;
}

export interface ChatItemRedirect extends BaseChatItem {
  open_in_new_tab: boolean;
  url: string;
}

export enum KBEntryViewType {
  SOURCE = 'source',
  CARD = 'card',
}

export interface ChatItemKBEntry extends BaseChatItem {
  type: ChatItemType.KB_ENTRY;
  kb_constant_id: string;
  title: string;
  description: string;
  view_type: KBEntryViewType;
  resultIndex?: number;
  result_type?: string;
}

export interface ChatItemAnalyticsTag extends BaseChatItem {
  type: ChatItemType.ANALYTICS_TAG;
  event_type: string;
  tag_id: string;
  tag_name: string;
  posthog_event_config?: string;
}

export interface LocationResult {
  address: string;
  city: string;
  closes_at?: string | null;
  distance: string;
  distance_miles: number;
  full_hours: {
    day: string;
    day_num: number;
    utc_open_time: string;
    utc_close_time: string;
  };
  name: string;
  open_now?: boolean;
  opens_at?: string | null;
  phone_number: string | null;
  place_id: string;
  source: 'google' | 'internal';
  state: string;
  tags: string[];
  zipcode: string;
}

export interface ChatItemLocationResults extends BaseChatItem {
  disclaimer_message?: string | null;
  filter_message: string;
  items: LocationResult[];
  origin_zip: string;
}

export interface ChatItemStaticImage extends BaseChatItem {
  type: ChatItemType.STATIC_IMAGE;
  url: string;
  link_url?: string;
  alignment?: 'left' | 'center' | 'right';
  scale?: number;
}

export interface ChatItemVideo extends BaseChatItem {
  type: ChatItemType.VIDEO;
  url: string;
  placeholder_image_url?: string;
  video_name?: string;
  title?: string;
}

export interface ChatItemFeedback extends BaseChatItem {
  type: ChatItemType.FEEDBACK;
}

export type ChatItem = (
  | ChatItemMessage
  | ChatItemForm
  | ChatItemRedirect
  | ChatItemAnalyticsTag
  | ChatItemLocationResults
  | ChatItemKBEntry
  | ChatItemStaticImage
  | ChatItemVideo
  | ChatItemFeedback
) & {
  userMessage?: boolean;
};
export type AnswerItem = ChatItemForm;

export interface ChatBotResponse {
  chat_items: ChatItem[];
  form?: ChatItemForm;
  opt_in?: boolean;
}

export interface ChatBotFormAnswer {
  form_ref_id: string;
  item_ref_id: string;
  value?: string;
}

export interface ChatBotUserMessage {
  content: string;
}

interface ApiResponse {
  chat_items: unknown[];
  [key: string]: unknown;
}

async function makeRequestWithRetry<T, R>(
  url: string,
  data: T,
  api: ReturnType<typeof makeApi>,
  processResponse: (responseData: ApiResponse) => R,
  requestIdentifier?: string
): Promise<R> {
  async function executeRequest(retryCount = 0): Promise<R> {
    const { requestConfig } = makeRequestWithAbort(url, 'POST', { timeout: 10000 }, requestIdentifier);

    try {
      const response = await api.post(url, data, requestConfig);
      return processResponse(response.data);
    } catch (error: Error | unknown) {
      if (error instanceof Error && (error.name === 'AbortError' || error.name === 'CanceledError')) {
        throw error;
      }

      if (retryCount < 1) {
        return executeRequest(retryCount + 1);
      }
      throw error;
    }
  }

  return executeRequest();
}

export const REQUEST_KEYS = {
  START_CHAT_BOT: (uuid?: string) => `startChatBot:${uuid || 'default'}`,
  SEND_ANSWER: (uuid: string) => `sendAnswer:${uuid}`,
  SEND_MESSAGE: (uuid: string) => `sendMessage:${uuid}`,
  DISMISS_QUESTION: (uuid: string) => `dismissQuestion:${uuid}`,
};

export async function startChatBot({
  uuid,
  action,
  searchQuery,
}: {
  uuid?: string;
  action?: string;
  searchQuery?: string;
}): Promise<ChatBotResponse> {
  const url = searchQuery
    ? `/api/v1/web_chat/v3.0/help-center/${uuid}/virtual-assistant/search/`
    : `/api/v1/web_chat/v3.0/help-center/${uuid}/virtual-assistant/`;

  const api = makeApi();
  const requestKey = REQUEST_KEYS.START_CHAT_BOT(uuid);

  const { requestConfig } = makeRequestWithAbort(url, 'POST', undefined, requestKey);

  const response = await api.post(
    url,
    {
      ...(action ? { action } : {}),
      ...(searchQuery ? { content: searchQuery } : {}),
    },
    requestConfig
  );

  return {
    ...response.data,
    chat_items: response.data.chat_items.map((item: ChatItem) => ({
      ...item,
      delay_duration: item.delay_duration * 1000,
      uuid: v4(),
      visible: false,
      aiResponse: !!searchQuery,
    })),
  };
}

export async function initChatBot({ uuid }: { uuid?: string }): Promise<ChatBotResponse> {
  const url = `/api/v1/web_chat/v3.0/help-center/${uuid}/virtual-assistant/init/`;

  const api = makeApi();
  const requestKey = REQUEST_KEYS.START_CHAT_BOT(uuid);

  const { requestConfig } = makeRequestWithAbort(url, 'POST', undefined, requestKey);

  const response = await api.post(url, {}, requestConfig);

  return {
    ...response.data,
    chat_items: response.data.chat_items.map((item: ChatItem) => ({
      ...item,
      delay_duration: item.delay_duration * 1000,
      uuid: v4(),
      visible: false,
      aiResponse: true,
    })),
  };
}

export async function sendAnswer({ uuid, data }: { uuid: string; data: ChatBotFormAnswer }): Promise<ChatBotResponse> {
  const url = `/api/v1/web_chat/v3.0/help-center/${uuid}/virtual-assistant/answer/`;
  const api = makeApi();
  const requestKey = REQUEST_KEYS.SEND_ANSWER(uuid);

  return makeRequestWithRetry(
    url,
    data,
    api,
    (responseData) => ({
      ...responseData,
      chat_items: responseData.chat_items.map((item) => {
        const chatItem = item as unknown as ChatItem;
        return {
          ...chatItem,
          delay_duration: chatItem.delay_duration * 1000,
          uuid: v4(),
          visible: false,
          aiResponse: false,
        };
      }),
      form: responseData.form as ChatItemForm | undefined,
    }),
    requestKey
  );
}

export async function sendMessage({
  uuid,
  data,
}: {
  uuid: string;
  data: ChatBotUserMessage;
}): Promise<ChatBotResponse> {
  const url = `/api/v1/web_chat/v3.0/help-center/${uuid}/virtual-assistant/message/`;
  const api = makeApi();
  const requestKey = REQUEST_KEYS.SEND_MESSAGE(uuid);

  return makeRequestWithRetry(
    url,
    data,
    api,
    (responseData) => ({
      ...responseData,
      chat_items: responseData.chat_items.map((item) => {
        const chatItem = item as unknown as ChatItem;
        return {
          ...chatItem,
          delay_duration: chatItem.delay_duration * 1000,
          uuid: v4(),
          visible: false,
          aiResponse: true,
        };
      }),
    }),
    requestKey
  );
}

export async function dismissQuestion({ uuid, refId }: { uuid: string; refId: string }): Promise<ChatBotResponse> {
  const url = `/api/v1/web_chat/v3.0/help-center/${uuid}/virtual-assistant/dismiss/`;
  const api = makeApi();
  const requestKey = REQUEST_KEYS.DISMISS_QUESTION(uuid);

  const { requestConfig } = makeRequestWithAbort(url, 'POST', undefined, requestKey);

  const response = await api.post(url, { form_ref_id: refId }, requestConfig);

  return {
    ...response.data,
    chat_items: response.data.chat_items.map((item: ChatItem) => ({
      ...item,
      delay_duration: item.delay_duration * 1000,
      uuid: v4(),
      visible: false,
      aiResponse: false,
    })),
  };
}

export function cancelChatBotRequests(uuid: string): void {
  cancelRequest(REQUEST_KEYS.START_CHAT_BOT(uuid));
  cancelRequest(REQUEST_KEYS.SEND_ANSWER(uuid));
  cancelRequest(REQUEST_KEYS.SEND_MESSAGE(uuid));
  cancelRequest(REQUEST_KEYS.DISMISS_QUESTION(uuid));
}
